import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Activity, 
  ArrowLeft, 
  Upload, 
  FileText, 
  Heart, 
  Brain, 
  Loader2,
  CheckCircle,
  XCircle,
  Trash2,
  Eye,
  Sparkles,
  Save,
  AlertTriangle,
  X
} from 'lucide-react';
import PrognosisFlowchart from '../components/PrognosisFlowchart';
import CreditsDisplay from '../components/CreditsDisplay';

const History = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('reports'); // reports, vitals, insights
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [dataChanged, setDataChanged] = useState(false);
  const [refreshCredits, setRefreshCredits] = useState(0);
  const [rateLimitError, setRateLimitError] = useState(null);
  
  // Vitals state
  const [vitals, setVitals] = useState({
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    pulse: '',
    temperature: '',
    spo2: '',
    respiratory_rate: '',
    blood_glucose: ''
  });
  const [vitalsSaving, setVitalsSaving] = useState(false);
  const [vitalsSaved, setVitalsSaved] = useState(false);
  
  // Personal history state
  const [personalHistory, setPersonalHistory] = useState({
    diet: '',
    weight: '',
    height: '',
    smoking: '',
    smoking_duration: '',
    alcohol: '',
    alcohol_frequency: '',
    sleep_hours: '',
    micturition: '',
    bowel_habits: '',
    physical_activity: '',
    allergies: '',
    chronic_conditions: ''
  });
  const [personalSaving, setPersonalSaving] = useState(false);
  const [personalSaved, setPersonalSaved] = useState(false);
  
  // Insights state
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  
  // Version tracking state
  const [versionHistory, setVersionHistory] = useState([]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [compareVersion1, setCompareVersion1] = useState(null);
  const [compareVersion2, setCompareVersion2] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReports();
    } else if (activeTab === 'vitals') {
      fetchVitalsAndPersonal();
    } else if (activeTab === 'insights') {
      fetchLatestInsights();
    }
  }, [activeTab]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/history/reports`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVitalsAndPersonal = async () => {
    try {
      setLoading(true);
      
      // Fetch vitals
      const vitalsRes = await fetch(`${backendUrl}/api/history/vitals`, {
        credentials: 'include'
      });
      if (vitalsRes.ok) {
        const vitalsData = await vitalsRes.json();
        setVitals({
          blood_pressure_systolic: vitalsData.blood_pressure_systolic || '',
          blood_pressure_diastolic: vitalsData.blood_pressure_diastolic || '',
          pulse: vitalsData.pulse || '',
          temperature: vitalsData.temperature || '',
          spo2: vitalsData.spo2 || '',
          respiratory_rate: vitalsData.respiratory_rate || '',
          blood_glucose: vitalsData.blood_glucose || ''
        });
      }
      
      // Fetch personal history
      const personalRes = await fetch(`${backendUrl}/api/history/personal`, {
        credentials: 'include'
      });
      if (personalRes.ok) {
        const personalData = await personalRes.json();
        setPersonalHistory({
          diet: personalData.diet || '',
          weight: personalData.weight || '',
          height: personalData.height || '',
          smoking: personalData.smoking || '',
          smoking_duration: personalData.smoking_duration || '',
          alcohol: personalData.alcohol || '',
          alcohol_frequency: personalData.alcohol_frequency || '',
          sleep_hours: personalData.sleep_hours || '',
          micturition: personalData.micturition || '',
          bowel_habits: personalData.bowel_habits || '',
          physical_activity: personalData.physical_activity || '',
          allergies: personalData.allergies || '',
          chronic_conditions: personalData.chronic_conditions || ''
        });
      }
    } catch (error) {
      console.error('Error fetching vitals/personal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload only PDF or image files (JPEG, PNG)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size should not exceed 10MB');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress('Uploading file...');

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${backendUrl}/api/history/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        setUploadProgress('Processing with AI...');
        const data = await response.json();
        
        setReports(prev => [data, ...prev]);
        setUploadProgress('✓ Upload successful!');
        setDataChanged(true);
        
        setTimeout(() => {
          setUploadProgress('');
          setUploading(false);
        }, 2000);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
      setUploading(false);
      setUploadProgress('');
    }

    event.target.value = '';
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/history/report/${reportId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setReports(prev => prev.filter(r => r.id !== reportId));
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Failed to delete report. Please try again.');
    }
  };

  const handleVitalsChange = (field, value) => {
    setVitals(prev => ({ ...prev, [field]: value }));
    setVitalsSaved(false);
    setDataChanged(true);
  };

  const handlePersonalChange = (field, value) => {
    setPersonalHistory(prev => ({ ...prev, [field]: value }));
    setPersonalSaved(false);
    setDataChanged(true);
  };

  const saveVitals = async () => {
    try {
      setVitalsSaving(true);
      
      // Convert empty strings to null for numeric fields
      const vitalsData = {
        blood_pressure_systolic: vitals.blood_pressure_systolic ? parseInt(vitals.blood_pressure_systolic) : null,
        blood_pressure_diastolic: vitals.blood_pressure_diastolic ? parseInt(vitals.blood_pressure_diastolic) : null,
        pulse: vitals.pulse ? parseInt(vitals.pulse) : null,
        temperature: vitals.temperature ? parseFloat(vitals.temperature) : null,
        spo2: vitals.spo2 ? parseInt(vitals.spo2) : null,
        respiratory_rate: vitals.respiratory_rate ? parseInt(vitals.respiratory_rate) : null,
        blood_glucose: vitals.blood_glucose ? parseInt(vitals.blood_glucose) : null
      };
      
      const response = await fetch(`${backendUrl}/api/history/vitals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(vitalsData)
      });
      
      if (response.ok) {
        setVitalsSaved(true);
        setTimeout(() => setVitalsSaved(false), 2000);
      }
    } catch (error) {
      console.error('Error saving vitals:', error);
      alert('Failed to save vitals. Please try again.');
    } finally {
      setVitalsSaving(false);
    }
  };

  const savePersonalHistory = async () => {
    try {
      setPersonalSaving(true);
      
      // Convert empty strings to null for numeric fields
      const personalData = {
        ...personalHistory,
        weight: personalHistory.weight ? parseFloat(personalHistory.weight) : null,
        height: personalHistory.height ? parseFloat(personalHistory.height) : null,
        sleep_hours: personalHistory.sleep_hours ? parseFloat(personalHistory.sleep_hours) : null
      };
      
      const response = await fetch(`${backendUrl}/api/history/personal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(personalData)
      });
      
      if (response.ok) {
        setPersonalSaved(true);
        setTimeout(() => setPersonalSaved(false), 2000);
      }
    } catch (error) {
      console.error('Error saving personal history:', error);
      alert('Failed to save personal history. Please try again.');
    } finally {
      setPersonalSaving(false);
    }
  };

  const fetchLatestInsights = async () => {
    try {
      setInsightsLoading(true);
      const response = await fetch(`${backendUrl}/api/history/insights/latest`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setInsights(data);
        } else {
          setInsights(null);
        }
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  const generateNewInsights = async () => {
    try {
      setGeneratingInsights(true);
      setRateLimitError(null);
      
      const response = await fetch(`${backendUrl}/api/history/insights/generate`, {
        method: 'POST',
        credentials: 'include'
      });
      
      // Handle 429 Rate Limit Error
      if (response.status === 429) {
        const errorData = await response.json().catch(() => ({}));
        setRateLimitError({
          message: errorData.detail || "You've reached your daily limit of AI queries. Please try again tomorrow.",
          type: 'rate_limit'
        });
        // Refresh credits to show updated count
        setRefreshCredits(prev => prev + 1);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setInsights(data);
          setDataChanged(false);
          // Refresh credits after successful generation
          setRefreshCredits(prev => prev + 1);
          alert('✓ Patient insights generated successfully!');
        } else if (data.status === 'insufficient_data') {
          alert(data.message + '\n\nSuggestions:\n' + data.suggestions.join('\n'));
        }
      } else {
        throw new Error('Failed to generate insights');
      }
    } catch (error) {
      console.error('Error generating insights:', error);
      alert('Failed to generate insights. Please try again.');
    } finally {
      setGeneratingInsights(false);
    }
  };

  const fetchVersionHistory = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/history/insights/versions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setVersionHistory(data.versions || []);
      }
    } catch (error) {
      console.error('Error fetching version history:', error);
    }
  };

  const loadVersion = async (versionId) => {
    try {
      setInsightsLoading(true);
      const response = await fetch(`${backendUrl}/api/history/insights/version/${versionId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedVersion(data);
        setShowVersionHistory(false);
      }
    } catch (error) {
      console.error('Error loading version:', error);
      alert('Failed to load version');
    } finally {
      setInsightsLoading(false);
    }
  };

  const compareVersions = async () => {
    if (!compareVersion1 || !compareVersion2) {
      alert('Please select two versions to compare');
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/history/insights/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          version1_id: compareVersion1,
          version2_id: compareVersion2
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setComparisonResult(data);
      }
    } catch (error) {
      console.error('Error comparing versions:', error);
      alert('Failed to compare versions');
    }
  };

  const deleteVersion = async (versionId) => {
    if (!window.confirm('Are you sure you want to delete this version?')) {
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/history/insights/version/${versionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setVersionHistory(prev => prev.filter(v => v.id !== versionId));
        alert('Version deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting version:', error);
      alert('Failed to delete version');
    }
  };

  const renderReportsTab = () => (
    <div>
      {/* Upload Section */}
      <div style={{
        background: 'linear-gradient(135deg, #0277bd 0%, #01579b 100%)',
        borderRadius: '16px',
        padding: '2rem',
        marginBottom: '2rem',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Upload size={28} />
            Upload Medical Reports
          </h3>
          <p style={{ opacity: 0.9, marginBottom: '1.5rem' }}>
            Upload lab reports, prescriptions, radiology scans, or discharge summaries
          </p>
          
          <label htmlFor="file-upload" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'white',
            color: '#0277bd',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            opacity: uploading ? 0.6 : 1,
            transition: 'transform 0.2s'
          }}>
            {uploading ? <Loader2 size={20} className="spinning" /> : <FileText size={20} />}
            {uploading ? 'Processing...' : 'Choose File'}
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          
          {uploadProgress && (
            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {uploadProgress.includes('✓') ? (
                <CheckCircle size={20} />
              ) : (
                <Loader2 size={20} className="spinning" />
              )}
              <span>{uploadProgress}</span>
            </div>
          )}
        </div>
        
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%'
        }} />
      </div>

      {/* Reports List */}
      <div>
        <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#0277bd' }}>
          Uploaded Reports ({reports.length})
        </h3>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Loader2 size={40} className="spinning" style={{ color: '#0277bd' }} />
            <p style={{ color: '#78909c', marginTop: '1rem' }}>Loading reports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '3rem 2rem',
            textAlign: 'center',
            border: '2px dashed #b0bec5'
          }}>
            <FileText size={48} style={{ color: '#b0bec5', marginBottom: '1rem' }} />
            <p style={{ color: '#78909c', fontSize: '1rem' }}>
              No reports uploaded yet. Upload your first medical report to get started!
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {reports.map(report => (
              <div
                key={report.id}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  border: '1px solid #e0e0e0',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <FileText size={24} style={{ color: '#0277bd' }} />
                      <h4 style={{ fontSize: '1.1rem', color: '#01579b', margin: 0 }}>
                        {report.file_name}
                      </h4>
                      {report.processing_status === 'completed' && (
                        <CheckCircle size={20} style={{ color: '#4caf50' }} />
                      )}
                      {report.processing_status === 'processing' && (
                        <Loader2 size={20} className="spinning" style={{ color: '#ff9800' }} />
                      )}
                      {report.processing_status === 'failed' && (
                        <XCircle size={20} style={{ color: '#f44336' }} />
                      )}
                    </div>
                    
                    <p style={{ color: '#78909c', fontSize: '0.9rem', margin: '0.25rem 0' }}>
                      Uploaded: {new Date(report.upload_date).toLocaleString()}
                    </p>
                    
                    {report.processing_status === 'failed' && (
                      <div style={{ 
                        marginTop: '1rem', 
                        padding: '1rem', 
                        background: '#ffebee', 
                        borderRadius: '8px',
                        border: '1px solid #ef5350'
                      }}>
                        <p style={{ fontWeight: 600, color: '#d32f2f', marginBottom: '0.25rem', fontSize: '0.95rem' }}>
                          ⚠️ Processing Failed
                        </p>
                        <p style={{ color: '#c62828', fontSize: '0.85rem' }}>
                          Unable to extract data from this file. Please try uploading again or use a different file format.
                        </p>
                      </div>
                    )}
                    
                    {report.extracted_data && report.processing_status === 'completed' && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
                        <p style={{ fontWeight: 600, color: '#0277bd', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                          ✨ AI Extracted Information:
                        </p>
                        
                        {report.extracted_data.diagnoses && report.extracted_data.diagnoses.length > 0 && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong style={{ color: '#01579b' }}>Diagnoses:</strong> {report.extracted_data.diagnoses.join(', ')}
                          </div>
                        )}
                        
                        {report.extracted_data.medications && report.extracted_data.medications.length > 0 && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong style={{ color: '#01579b' }}>Medications:</strong> {' '}
                            {report.extracted_data.medications.map((m, idx) => {
                              if (typeof m === 'object' && m.name) {
                                return `${m.name}${m.dose ? ` (${m.dose})` : ''}`;
                              }
                              return m;
                            }).join(', ')}
                          </div>
                        )}
                        
                        {report.extracted_data.symptoms && report.extracted_data.symptoms.length > 0 && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong style={{ color: '#01579b' }}>Symptoms:</strong> {report.extracted_data.symptoms.join(', ')}
                          </div>
                        )}
                        
                        {report.extracted_data.lab_values && report.extracted_data.lab_values.length > 0 && (
                          <div>
                            <strong style={{ color: '#01579b' }}>Lab Results:</strong>
                            <div style={{ marginTop: '0.5rem' }}>
                              {report.extracted_data.lab_values.map((lab, idx) => (
                                <div key={idx} style={{ marginLeft: '1rem', fontSize: '0.9rem', color: '#37474f' }}>
                                  • {lab.test_name}: {lab.value} {lab.status && `(${lab.status})`}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {report.extracted_data.doctor_name && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#607d8b' }}>
                            <strong>Doctor:</strong> {report.extracted_data.doctor_name}
                          </div>
                        )}
                        
                        {report.extracted_data.hospital && (
                          <div style={{ fontSize: '0.85rem', color: '#607d8b' }}>
                            <strong>Hospital:</strong> {report.extracted_data.hospital}
                          </div>
                        )}
                        
                        {!report.extracted_data.diagnoses?.length && 
                         !report.extracted_data.medications?.length && 
                         !report.extracted_data.symptoms?.length && 
                         !report.extracted_data.lab_values?.length && (
                          <div style={{ padding: '0.75rem', background: '#fff3e0', borderRadius: '6px', marginTop: '0.5rem' }}>
                            <p style={{ color: '#e65100', fontSize: '0.9rem', margin: 0 }}>
                              ℹ️ No medical information could be extracted from this document.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => handleDeleteReport(report.id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#f44336',
                      cursor: 'pointer',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#ffebee'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderVitalsTab = () => (
    <div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Loader2 size={40} className="spinning" style={{ color: '#0277bd' }} />
          <p style={{ color: '#78909c', marginTop: '1rem' }}>Loading data...</p>
        </div>
      ) : (
        <>
          {/* Vitals Section */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            marginBottom: '2rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', color: '#0277bd', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Heart size={28} />
                Vital Signs
              </h3>
              <button
                onClick={saveVitals}
                disabled={vitalsSaving}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: vitalsSaved ? '#4caf50' : '#0277bd',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: vitalsSaving ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  transition: 'background 0.2s'
                }}
              >
                {vitalsSaving ? (
                  <><Loader2 size={18} className="spinning" /> Saving...</>
                ) : vitalsSaved ? (
                  <><CheckCircle size={18} /> Saved!</>
                ) : (
                  <><Save size={18} /> Save Vitals</>
                )}
              </button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              {/* Blood Pressure */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Blood Pressure (mmHg)
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    placeholder="Systolic"
                    value={vitals.blood_pressure_systolic}
                    onChange={(e) => handleVitalsChange('blood_pressure_systolic', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <span style={{ color: '#78909c', fontWeight: 600 }}>/</span>
                  <input
                    type="number"
                    placeholder="Diastolic"
                    value={vitals.blood_pressure_diastolic}
                    onChange={(e) => handleVitalsChange('blood_pressure_diastolic', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>
              
              {/* Pulse */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Pulse (bpm)
                </label>
                <input
                  type="number"
                  placeholder="e.g., 75"
                  value={vitals.pulse}
                  onChange={(e) => handleVitalsChange('pulse', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              {/* Temperature */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Temperature (°C)
                </label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g., 98.6"
                  value={vitals.temperature}
                  onChange={(e) => handleVitalsChange('temperature', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              {/* SpO2 */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  SpO₂ (%)
                </label>
                <input
                  type="number"
                  placeholder="e.g., 98"
                  value={vitals.spo2}
                  onChange={(e) => handleVitalsChange('spo2', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              {/* Respiratory Rate */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Respiratory Rate (/min)
                </label>
                <input
                  type="number"
                  placeholder="e.g., 16"
                  value={vitals.respiratory_rate}
                  onChange={(e) => handleVitalsChange('respiratory_rate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              {/* Blood Glucose */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Blood Glucose (mg/dL)
                </label>
                <input
                  type="number"
                  placeholder="e.g., 110"
                  value={vitals.blood_glucose}
                  onChange={(e) => handleVitalsChange('blood_glucose', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Personal History Section */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', color: '#0277bd' }}>
                Personal Health History
              </h3>
              <button
                onClick={savePersonalHistory}
                disabled={personalSaving}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: personalSaved ? '#4caf50' : '#0277bd',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: personalSaving ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  transition: 'background 0.2s'
                }}
              >
                {personalSaving ? (
                  <><Loader2 size={18} className="spinning" /> Saving...</>
                ) : personalSaved ? (
                  <><CheckCircle size={18} /> Saved!</>
                ) : (
                  <><Save size={18} /> Save History</>
                )}
              </button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              {/* Diet */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Diet
                </label>
                <select
                  value={personalHistory.diet}
                  onChange={(e) => handlePersonalChange('diet', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                >
                  <option value="">Select diet type</option>
                  <option value="Vegetarian">Vegetarian</option>
                  <option value="Non-Vegetarian">Non-Vegetarian</option>
                  <option value="Mixed">Mixed</option>
                  <option value="Vegan">Vegan</option>
                </select>
              </div>
              
              {/* Weight */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Weight (kg)
                </label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g., 70"
                  value={personalHistory.weight}
                  onChange={(e) => handlePersonalChange('weight', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              {/* Height */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Height (cm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g., 170"
                  value={personalHistory.height}
                  onChange={(e) => handlePersonalChange('height', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              {/* Smoking */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Smoking
                </label>
                <select
                  value={personalHistory.smoking}
                  onChange={(e) => handlePersonalChange('smoking', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                >
                  <option value="">Select</option>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                  <option value="Former">Former</option>
                </select>
              </div>
              
              {/* Smoking Duration */}
              {personalHistory.smoking !== 'No' && personalHistory.smoking && (
                <div>
                  <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                    Smoking Duration
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 5 years"
                    value={personalHistory.smoking_duration}
                    onChange={(e) => handlePersonalChange('smoking_duration', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              )}
              
              {/* Alcohol */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Alcohol
                </label>
                <select
                  value={personalHistory.alcohol}
                  onChange={(e) => handlePersonalChange('alcohol', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                >
                  <option value="">Select</option>
                  <option value="No">No</option>
                  <option value="Occasional">Occasional</option>
                  <option value="Regular">Regular</option>
                </select>
              </div>
              
              {/* Alcohol Frequency */}
              {personalHistory.alcohol !== 'No' && personalHistory.alcohol && (
                <div>
                  <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                    Alcohol Frequency
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 2-3 times per week"
                    value={personalHistory.alcohol_frequency}
                    onChange={(e) => handlePersonalChange('alcohol_frequency', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              )}
              
              {/* Sleep Hours */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Sleep (hours/day)
                </label>
                <input
                  type="number"
                  step="0.5"
                  placeholder="e.g., 7"
                  value={personalHistory.sleep_hours}
                  onChange={(e) => handlePersonalChange('sleep_hours', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              {/* Micturition */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Micturition
                </label>
                <select
                  value={personalHistory.micturition}
                  onChange={(e) => handlePersonalChange('micturition', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                >
                  <option value="">Select</option>
                  <option value="Normal">Normal</option>
                  <option value="Frequent">Frequent</option>
                  <option value="Reduced">Reduced</option>
                </select>
              </div>
              
              {/* Bowel Habits */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Bowel Habits
                </label>
                <select
                  value={personalHistory.bowel_habits}
                  onChange={(e) => handlePersonalChange('bowel_habits', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                >
                  <option value="">Select</option>
                  <option value="Normal">Normal</option>
                  <option value="Constipation">Constipation</option>
                  <option value="Diarrhea">Diarrhea</option>
                </select>
              </div>
              
              {/* Physical Activity */}
              <div>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Physical Activity
                </label>
                <select
                  value={personalHistory.physical_activity}
                  onChange={(e) => handlePersonalChange('physical_activity', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    background: 'white'
                  }}
                >
                  <option value="">Select</option>
                  <option value="Sedentary">Sedentary</option>
                  <option value="Light">Light</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Active">Active</option>
                </select>
              </div>
              
              {/* Allergies */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Allergies
                </label>
                <textarea
                  placeholder="List any known allergies..."
                  value={personalHistory.allergies}
                  onChange={(e) => handlePersonalChange('allergies', e.target.value)}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              {/* Chronic Conditions */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', color: '#01579b', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Chronic Conditions
                </label>
                <textarea
                  placeholder="List any chronic health conditions..."
                  value={personalHistory.chronic_conditions}
                  onChange={(e) => handlePersonalChange('chronic_conditions', e.target.value)}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderInsightsTab = () => {
    if (insightsLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <Loader2 size={48} className="spinning" style={{ color: '#0277bd' }} />
          <p style={{ color: '#78909c', marginTop: '1rem' }}>Loading patient insights...</p>
        </div>
      );
    }

    if (!insights || insights.status === 'no_insights') {
      return (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '3rem 2rem',
          textAlign: 'center'
        }}>
          <Brain size={64} style={{ color: '#0277bd', marginBottom: '1.5rem' }} />
          <h3 style={{ color: '#0277bd', fontSize: '1.8rem', marginBottom: '1rem' }}>
            Generate Your Patient Intelligence Profile
          </h3>
          <p style={{ color: '#78909c', maxWidth: '600px', margin: '0 auto 2rem' }}>
            AI will analyze all your medical reports, vitals, and personal health history to create a comprehensive patient profile with risk predictions and personalized recommendations.
          </p>
          <button
            onClick={generateNewInsights}
            disabled={generatingInsights}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(135deg, #0277bd 0%, #01579b 100%)',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '12px',
              fontSize: '1.1rem',
              fontWeight: 600,
              cursor: generatingInsights ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(2, 119, 189, 0.3)',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => !generatingInsights && (e.currentTarget.style.transform = 'scale(1.05)')}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            {generatingInsights ? (
              <><Loader2 size={20} className="spinning" /> Generating Insights...</>
            ) : (
              <><Sparkles size={20} /> Generate AI Insights</>
            )}
          </button>
        </div>
      );
    }

    const { patient_profile, risk_assessment, prognosis_flow, lifestyle_modifications, treatment_guidance, missing_data_suggestions, medical_timeline } = insights.insights_data || insights;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Header with Regenerate & View History Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ fontSize: '1.8rem', color: '#0277bd', margin: 0 }}>Patient Intelligence Profile</h2>
            <p style={{ color: '#78909c', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              Generated: {new Date(insights.generated_date).toLocaleString()}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              onClick={async () => {
                try {
                  const response = await fetch(`${backendUrl}/api/history/insights/download-pdf`, {
                    credentials: 'include'
                  });
                  if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `MedPlexity_Health_Report_${new Date().toISOString().slice(0,10)}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();
                  } else {
                    alert('Failed to download PDF. Please try again.');
                  }
                } catch (error) {
                  console.error('Error downloading PDF:', error);
                  alert('Failed to download PDF. Please try again.');
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(76, 175, 80, 0.3)';
              }}
            >
              <FileText size={18} />
              Download PDF Report
            </button>
            <button
              onClick={() => {
                setShowVersionHistory(!showVersionHistory);
                if (!showVersionHistory) fetchVersionHistory();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: showVersionHistory ? '#ff9800' : 'white',
                color: showVersionHistory ? 'white' : '#0277bd',
                border: '2px solid #0277bd',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              <Activity size={18} />
              {showVersionHistory ? 'Hide' : 'View'} History
            </button>
            <button
              onClick={generateNewInsights}
              disabled={generatingInsights}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: '#0277bd',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
              cursor: generatingInsights ? 'not-allowed' : 'pointer',
              fontWeight: 600
            }}
          >
            {generatingInsights ? <Loader2 size={18} className="spinning" /> : <Sparkles size={18} />}
            Regenerate
          </button>
          </div>
        </div>

        {/* Version History Panel */}
        {showVersionHistory && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '2px solid #0277bd' }}>
            <h3 style={{ color: '#0277bd', fontSize: '1.3rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Activity size={24} />
              Version History ({versionHistory.length} versions)
            </h3>
            
            {!comparisonMode ? (
              <>
                <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setComparisonMode(true)}
                    style={{
                      background: '#ff9800',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 600
                    }}
                  >
                    Compare Versions
                  </button>
                </div>
                
                {/* Version Timeline */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '400px', overflowY: 'auto' }}>
                  {versionHistory.map((version, idx) => (
                    <div
                      key={version.id}
                      style={{
                        background: idx === 0 ? '#e3f2fd' : '#f5f5f5',
                        padding: '1rem',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${idx === 0 ? '#0277bd' : '#b0bec5'}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: '#01579b', marginBottom: '0.25rem' }}>
                          {idx === 0 ? '📍 Current Version' : `Version ${versionHistory.length - idx}`}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#607d8b' }}>
                          {new Date(version.generated_date).toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#78909c', marginTop: '0.25rem' }}>
                          Reports: {version.data_version?.reports_count || 0} • 
                          {version.data_version?.vitals_updated ? ' Vitals ✓' : ' No vitals'} • 
                          {version.data_version?.personal_updated ? ' Personal ✓' : ' No personal data'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {idx !== 0 && (
                          <>
                            <button
                              onClick={() => loadVersion(version.id)}
                              style={{
                                background: '#4caf50',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 600
                              }}
                            >
                              <Eye size={14} /> View
                            </button>
                            <button
                              onClick={() => deleteVersion(version.id)}
                              style={{
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 0.75rem',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                {/* Comparison Mode */}
                <div style={{ marginBottom: '1rem' }}>
                  <button
                    onClick={() => {
                      setComparisonMode(false);
                      setCompareVersion1(null);
                      setCompareVersion2(null);
                      setComparisonResult(null);
                    }}
                    style={{
                      background: '#607d8b',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    ← Back to History
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: '#01579b' }}>
                      Select Version 1 (Older):
                    </label>
                    <select
                      value={compareVersion1 || ''}
                      onChange={(e) => setCompareVersion1(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        border: '2px solid #e0e0e0'
                      }}
                    >
                      <option value="">Choose version...</option>
                      {versionHistory.map((v, idx) => (
                        <option key={v.id} value={v.id}>
                          {idx === 0 ? 'Current' : `Version ${versionHistory.length - idx}`} - {new Date(v.generated_date).toLocaleDateString()}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: '#01579b' }}>
                      Select Version 2 (Newer):
                    </label>
                    <select
                      value={compareVersion2 || ''}
                      onChange={(e) => setCompareVersion2(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        border: '2px solid #e0e0e0'
                      }}
                    >
                      <option value="">Choose version...</option>
                      {versionHistory.map((v, idx) => (
                        <option key={v.id} value={v.id}>
                          {idx === 0 ? 'Current' : `Version ${versionHistory.length - idx}`} - {new Date(v.generated_date).toLocaleDateString()}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <button
                  onClick={compareVersions}
                  disabled={!compareVersion1 || !compareVersion2}
                  style={{
                    background: '#0277bd',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: (!compareVersion1 || !compareVersion2) ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    opacity: (!compareVersion1 || !compareVersion2) ? 0.5 : 1
                  }}
                >
                  Compare Versions
                </button>
                
                {/* Comparison Results */}
                {comparisonResult && (
                  <div style={{ marginTop: '1.5rem', padding: '1.5rem', background: '#e8f5e9', borderRadius: '8px', border: '2px solid #4caf50' }}>
                    <h4 style={{ color: '#2e7d32', marginBottom: '1rem' }}>Comparison Results</h4>
                    
                    {comparisonResult.changes.overall_risk_changed && (
                      <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'white', borderRadius: '6px' }}>
                        <strong style={{ color: '#01579b' }}>Overall Risk Level:</strong><br/>
                        {comparisonResult.changes.overall_risk_from} → {comparisonResult.changes.overall_risk_to}
                        {comparisonResult.changes.overall_risk_from === 'High' && comparisonResult.changes.overall_risk_to === 'Moderate' && ' ✅ Improved'}
                        {comparisonResult.changes.overall_risk_from === 'Moderate' && comparisonResult.changes.overall_risk_to === 'High' && ' ⚠️ Increased'}
                      </div>
                    )}
                    
                    {comparisonResult.changes.new_diagnoses?.length > 0 && (
                      <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'white', borderRadius: '6px' }}>
                        <strong style={{ color: '#01579b' }}>New Diagnoses:</strong>
                        <ul style={{ margin: '0.5rem 0 0 1.5rem' }}>
                          {comparisonResult.changes.new_diagnoses.map((d, idx) => (
                            <li key={idx}>{d}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {comparisonResult.changes.new_risk_factors?.length > 0 && (
                      <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'white', borderRadius: '6px' }}>
                        <strong style={{ color: '#01579b' }}>New Risk Factors:</strong>
                        <ul style={{ margin: '0.5rem 0 0 1.5rem' }}>
                          {comparisonResult.changes.new_risk_factors.map((rf, idx) => (
                            <li key={idx}>{rf}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {comparisonResult.changes.resolved_risk_factors?.length > 0 && (
                      <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'white', borderRadius: '6px' }}>
                        <strong style={{ color: '#4caf50' }}>✅ Resolved Risk Factors:</strong>
                        <ul style={{ margin: '0.5rem 0 0 1.5rem' }}>
                          {comparisonResult.changes.resolved_risk_factors.map((rf, idx) => (
                            <li key={idx}>{rf}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {comparisonResult.changes.data_completeness_improved && (
                      <div style={{ padding: '0.75rem', background: 'white', borderRadius: '6px' }}>
                        <strong style={{ color: '#4caf50' }}>✅ Data Completeness Improved</strong>
                      </div>
                    )}
                    
                    {!comparisonResult.changes.has_changes && (
                      <div style={{ padding: '0.75rem', background: 'white', borderRadius: '6px', color: '#607d8b' }}>
                        No significant changes detected between these versions.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Patient Profile Section */}
        {patient_profile && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ color: '#0277bd', fontSize: '1.3rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Activity size={24} />
              Patient Profile
            </h3>
            
            {patient_profile.demographics && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ color: '#01579b', fontSize: '1.1rem', marginBottom: '0.5rem' }}>Health Status</h4>
                <p style={{ color: '#37474f', lineHeight: 1.6 }}>{patient_profile.demographics.health_status_summary}</p>
              </div>
            )}

            {patient_profile.present_illness && patient_profile.present_illness.current_complaints?.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ color: '#01579b', fontSize: '1.1rem', marginBottom: '0.5rem' }}>Current Health Concerns</h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#37474f' }}>
                  {patient_profile.present_illness.current_complaints.map((complaint, idx) => (
                    <li key={idx} style={{ marginBottom: '0.25rem' }}>{complaint}</li>
                  ))}
                </ul>
                {patient_profile.present_illness.timeline && (
                  <p style={{ marginTop: '0.5rem', color: '#607d8b', fontSize: '0.9rem' }}>
                    <strong>Timeline:</strong> {patient_profile.present_illness.timeline}
                  </p>
                )}
              </div>
            )}

            {patient_profile.current_medications?.length > 0 && (
              <div>
                <h4 style={{ color: '#01579b', fontSize: '1.1rem', marginBottom: '0.5rem' }}>Current Medications</h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {patient_profile.current_medications.map((med, idx) => (
                    <div key={idx} style={{ background: '#f5f5f5', padding: '0.75rem', borderRadius: '8px' }}>
                      <div style={{ fontWeight: 600, color: '#0277bd' }}>{med.name}</div>
                      {med.dose && <div style={{ fontSize: '0.9rem', color: '#607d8b' }}>Dose: {med.dose}</div>}
                      {med.frequency && <div style={{ fontSize: '0.9rem', color: '#607d8b' }}>Frequency: {med.frequency}</div>}
                      {med.indication && <div style={{ fontSize: '0.9rem', color: '#78909c', marginTop: '0.25rem' }}>For: {med.indication}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Risk Assessment with Advanced Scoring */}
        {risk_assessment && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ color: '#0277bd', fontSize: '1.3rem', marginBottom: '1.5rem' }}>Advanced Risk Assessment</h3>
            
            {/* Overall Risk Score Gauge */}
            {risk_assessment.overall_risk_score !== undefined && (
              <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f5f5f5', borderRadius: '12px' }}>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#01579b', marginBottom: '0.5rem' }}>
                    Overall Health Risk Score
                  </div>
                  <div style={{ fontSize: '3rem', fontWeight: 700, color: risk_assessment.overall_risk_score >= 70 ? '#f44336' : risk_assessment.overall_risk_score >= 40 ? '#ff9800' : '#4caf50' }}>
                    {risk_assessment.overall_risk_score}
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#78909c' }}>
                    out of 100
                  </div>
                </div>
                
                {/* Visual Progress Bar */}
                <div style={{ width: '100%', height: '20px', background: '#e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ 
                    width: `${risk_assessment.overall_risk_score}%`, 
                    height: '100%', 
                    background: risk_assessment.overall_risk_score >= 70 ? 'linear-gradient(90deg, #f44336, #d32f2f)' : 
                               risk_assessment.overall_risk_score >= 40 ? 'linear-gradient(90deg, #ff9800, #f57c00)' : 
                               'linear-gradient(90deg, #4caf50, #388e3c)',
                    transition: 'width 1s ease'
                  }} />
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.85rem', color: '#78909c' }}>
                  <span>0 (Low)</span>
                  <span>50 (Moderate)</span>
                  <span>100 (High)</span>
                </div>
                
                <div style={{ 
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  background: risk_assessment.overall_risk_level === 'High' ? '#ffebee' : 
                             risk_assessment.overall_risk_level === 'Moderate' ? '#fff3e0' : '#e8f5e9',
                  color: risk_assessment.overall_risk_level === 'High' ? '#c62828' : 
                         risk_assessment.overall_risk_level === 'Moderate' ? '#e65100' : '#2e7d32',
                  fontWeight: 600,
                  textAlign: 'center'
                }}>
                  Risk Level: {risk_assessment.overall_risk_level}
                </div>
              </div>
            )}

            {/* Category Risk Scores */}
            {risk_assessment.risk_scores && (
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#01579b', fontSize: '1.1rem', marginBottom: '1rem' }}>Risk by Category</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                  {Object.entries(risk_assessment.risk_scores).map(([category, data]) => {
                    const score = data.score || 0;
                    const level = data.level || 'Unknown';
                    const color = score >= 70 ? '#f44336' : score >= 40 ? '#ff9800' : '#4caf50';
                    
                    return (
                      <div key={category} style={{ 
                        padding: '1rem', 
                        background: 'white', 
                        border: '2px solid #e0e0e0',
                        borderRadius: '10px',
                        transition: 'transform 0.2s, box-shadow 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <div style={{ fontWeight: 600, color: '#01579b', marginBottom: '0.75rem', fontSize: '1rem' }}>
                          {category.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </div>
                        
                        {/* Circular gauge */}
                        <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                          <div style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            background: `conic-gradient(${color} ${score * 3.6}deg, #e0e0e0 ${score * 3.6}deg)`,
                            position: 'relative'
                          }}>
                            <div style={{
                              width: '64px',
                              height: '64px',
                              borderRadius: '50%',
                              background: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              color: color
                            }}>
                              {score}
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ 
                          padding: '0.5rem',
                          borderRadius: '6px',
                          background: score >= 70 ? '#ffebee' : score >= 40 ? '#fff3e0' : '#e8f5e9',
                          color: score >= 70 ? '#c62828' : score >= 40 ? '#e65100' : '#2e7d32',
                          fontWeight: 600,
                          textAlign: 'center',
                          fontSize: '0.9rem',
                          marginBottom: '0.75rem'
                        }}>
                          {level} Risk
                        </div>
                        
                        {data.factors && data.factors.length > 0 && (
                          <div style={{ fontSize: '0.85rem', color: '#607d8b' }}>
                            <strong>Key Factors:</strong>
                            <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                              {data.factors.slice(0, 2).map((factor, idx) => (
                                <li key={idx}>{factor}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Risk Factors List */}
            {risk_assessment.risk_factors?.length > 0 && (
              <div>
                <h4 style={{ color: '#01579b', fontSize: '1rem', marginBottom: '0.75rem' }}>Detailed Risk Factors:</h4>
                {risk_assessment.risk_factors.sort((a, b) => (b.priority || 0) - (a.priority || 0)).map((factor, idx) => (
                  <div key={idx} style={{ 
                    marginBottom: '0.75rem', 
                    padding: '1rem',
                    borderRadius: '8px',
                    background: '#f5f5f5',
                    borderLeft: `4px solid ${factor.level === 'High' ? '#f44336' : factor.level === 'Moderate' ? '#ff9800' : '#4caf50'}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <div style={{ fontWeight: 600, color: '#01579b' }}>{factor.factor}</div>
                      <div style={{ 
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        background: factor.level === 'High' ? '#ffebee' : factor.level === 'Moderate' ? '#fff3e0' : '#e8f5e9',
                        color: factor.level === 'High' ? '#c62828' : factor.level === 'Moderate' ? '#e65100' : '#2e7d32',
                        fontSize: '0.85rem',
                        fontWeight: 600
                      }}>
                        {factor.level}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#607d8b' }}>{factor.description}</div>
                    {factor.priority && (
                      <div style={{ fontSize: '0.8rem', color: '#78909c', marginTop: '0.25rem' }}>
                        Priority: {factor.priority}/10
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Enhanced Interactive Prognosis Flowchart */}
        {prognosis_flow && (
          <PrognosisFlowchart prognosisFlow={prognosis_flow} />
        )}

        {/* Lifestyle Modifications */}
        {lifestyle_modifications && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ color: '#0277bd', fontSize: '1.3rem', marginBottom: '1rem' }}>Lifestyle Recommendations</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              {lifestyle_modifications.diet && (
                <div style={{ padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
                  <div style={{ fontWeight: 600, color: '#01579b', marginBottom: '0.5rem' }}>🥗 Diet</div>
                  <div style={{ fontSize: '0.9rem', color: '#37474f' }}>{lifestyle_modifications.diet}</div>
                </div>
              )}
              {lifestyle_modifications.exercise && (
                <div style={{ padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
                  <div style={{ fontWeight: 600, color: '#01579b', marginBottom: '0.5rem' }}>🏃 Exercise</div>
                  <div style={{ fontSize: '0.9rem', color: '#37474f' }}>{lifestyle_modifications.exercise}</div>
                </div>
              )}
              {lifestyle_modifications.sleep && (
                <div style={{ padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
                  <div style={{ fontWeight: 600, color: '#01579b', marginBottom: '0.5rem' }}>😴 Sleep</div>
                  <div style={{ fontSize: '0.9rem', color: '#37474f' }}>{lifestyle_modifications.sleep}</div>
                </div>
              )}
              {lifestyle_modifications.stress_management && (
                <div style={{ padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
                  <div style={{ fontWeight: 600, color: '#01579b', marginBottom: '0.5rem' }}>🧘 Stress Management</div>
                  <div style={{ fontSize: '0.9rem', color: '#37474f' }}>{lifestyle_modifications.stress_management}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Multi-Pathy Treatment Protocols */}
        {insights.multi_pathy_protocols && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ color: '#0277bd', fontSize: '1.3rem', marginBottom: '0.5rem' }}>🌿 Multi-Pathy Treatment Protocols</h3>
            <p style={{ color: '#607d8b', fontSize: '0.9rem', marginBottom: '1.5rem', fontStyle: 'italic' }}>
              Explore parallel treatment approaches from different medical systems. For educational purposes only.
            </p>

            {/* Educational Disclaimer Banner */}
            <div style={{ 
              padding: '1rem', 
              background: '#fff3e0', 
              borderRadius: '8px', 
              border: '2px solid #ff9800',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontWeight: 600, color: '#e65100', marginBottom: '0.25rem' }}>⚠️ Important Disclaimer</div>
              <div style={{ fontSize: '0.9rem', color: '#37474f' }}>
                {insights.multi_pathy_protocols.educational_disclaimer || 
                 "All alternative medicine recommendations are for educational purposes only. Always consult qualified practitioners before starting any treatment."}
              </div>
            </div>

            {/* Tabs for Different Systems */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              {['allopathy', 'ayurveda', 'homeopathy', 'naturopathy', 'lifestyle_medicine'].map(system => (
                insights.multi_pathy_protocols[system] && (
                  <button
                    key={system}
                    onClick={() => {
                      const element = document.getElementById(`protocol-${system}`);
                      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '20px',
                      border: '2px solid #0277bd',
                      background: 'white',
                      color: '#0277bd',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#0277bd';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.color = '#0277bd';
                    }}
                  >
                    {system.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </button>
                )
              ))}
            </div>

            {/* Allopathy (Conventional Medicine) */}
            {insights.multi_pathy_protocols.allopathy && (
              <div id="protocol-allopathy" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f5f5f5', borderRadius: '12px', border: '2px solid #0277bd' }}>
                <h4 style={{ color: '#0277bd', fontSize: '1.2rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  💊 Allopathy (Conventional Medicine)
                  <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#4caf50', color: 'white', borderRadius: '12px' }}>
                    High Evidence
                  </span>
                </h4>
                
                {insights.multi_pathy_protocols.allopathy.approach && (
                  <p style={{ color: '#37474f', marginBottom: '1rem', lineHeight: 1.6 }}>
                    {insights.multi_pathy_protocols.allopathy.approach}
                  </p>
                )}

                {insights.multi_pathy_protocols.allopathy.medications?.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#01579b' }}>Medications:</strong>
                    <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.5rem' }}>
                      {insights.multi_pathy_protocols.allopathy.medications.map((med, idx) => (
                        <div key={idx} style={{ padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                          <div style={{ fontWeight: 600, color: '#0277bd' }}>{med.name}</div>
                          {med.category && <div style={{ fontSize: '0.85rem', color: '#78909c' }}>Category: {med.category}</div>}
                          {med.purpose && <div style={{ fontSize: '0.9rem', color: '#37474f', marginTop: '0.25rem' }}>{med.purpose}</div>}
                          {med.evidence_level && (
                            <span style={{ 
                              display: 'inline-block',
                              marginTop: '0.5rem',
                              padding: '0.25rem 0.5rem', 
                              background: med.evidence_level === 'High' ? '#4caf50' : med.evidence_level === 'Moderate' ? '#ff9800' : '#9e9e9e',
                              color: 'white',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>
                              Evidence: {med.evidence_level}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Ayurveda */}
            {insights.multi_pathy_protocols.ayurveda && (
              <div id="protocol-ayurveda" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#fff8e1', borderRadius: '12px', border: '2px solid #ff9800' }}>
                <h4 style={{ color: '#e65100', fontSize: '1.2rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🌿 Ayurveda (Traditional Indian Medicine)
                  <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#ff9800', color: 'white', borderRadius: '12px' }}>
                    Traditional
                  </span>
                </h4>

                {insights.multi_pathy_protocols.ayurveda.dosha_analysis && (
                  <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', marginBottom: '1rem' }}>
                    <strong style={{ color: '#e65100' }}>Dosha Analysis:</strong> {insights.multi_pathy_protocols.ayurveda.dosha_analysis}
                  </div>
                )}

                {insights.multi_pathy_protocols.ayurveda.herbs?.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#e65100' }}>Recommended Herbs:</strong>
                    <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.5rem' }}>
                      {insights.multi_pathy_protocols.ayurveda.herbs.map((herb, idx) => (
                        <div key={idx} style={{ padding: '0.75rem', background: 'white', borderRadius: '8px' }}>
                          <div style={{ fontWeight: 600, color: '#e65100' }}>{herb.name}</div>
                          {herb.properties && <div style={{ fontSize: '0.85rem', color: '#78909c' }}>{herb.properties}</div>}
                          {herb.benefits && <div style={{ fontSize: '0.9rem', color: '#37474f', marginTop: '0.25rem' }}>{herb.benefits}</div>}
                          {herb.dosage && <div style={{ fontSize: '0.85rem', color: '#607d8b', marginTop: '0.25rem' }}>Dosage: {herb.dosage}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {insights.multi_pathy_protocols.ayurveda.therapies?.length > 0 && (
                  <div style={{ padding: '1rem', background: 'white', borderRadius: '8px' }}>
                    <strong style={{ color: '#e65100' }}>Recommended Therapies:</strong>
                    <ul style={{ margin: '0.5rem 0 0 1.5rem', color: '#37474f' }}>
                      {insights.multi_pathy_protocols.ayurveda.therapies.map((therapy, idx) => (
                        <li key={idx}>{therapy}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Homeopathy */}
            {insights.multi_pathy_protocols.homeopathy && (
              <div id="protocol-homeopathy" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f3e5f5', borderRadius: '12px', border: '2px solid #9c27b0' }}>
                <h4 style={{ color: '#6a1b9a', fontSize: '1.2rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  💧 Homeopathy
                  <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#9c27b0', color: 'white', borderRadius: '12px' }}>
                    Traditional
                  </span>
                </h4>

                {insights.multi_pathy_protocols.homeopathy.constitutional_remedy && (
                  <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', marginBottom: '1rem' }}>
                    <strong style={{ color: '#6a1b9a' }}>Constitutional Remedy:</strong> {insights.multi_pathy_protocols.homeopathy.constitutional_remedy}
                  </div>
                )}

                {insights.multi_pathy_protocols.homeopathy.remedies?.length > 0 && (
                  <div>
                    <strong style={{ color: '#6a1b9a' }}>Recommended Remedies:</strong>
                    <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.5rem' }}>
                      {insights.multi_pathy_protocols.homeopathy.remedies.map((remedy, idx) => (
                        <div key={idx} style={{ padding: '0.75rem', background: 'white', borderRadius: '8px' }}>
                          <div style={{ fontWeight: 600, color: '#6a1b9a' }}>{remedy.name}</div>
                          {remedy.potency && <div style={{ fontSize: '0.85rem', color: '#78909c' }}>Potency: {remedy.potency}</div>}
                          {remedy.indications && <div style={{ fontSize: '0.9rem', color: '#37474f', marginTop: '0.25rem' }}>{remedy.indications}</div>}
                          {remedy.dosage && <div style={{ fontSize: '0.85rem', color: '#607d8b', marginTop: '0.25rem' }}>Dosage: {remedy.dosage}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Naturopathy */}
            {insights.multi_pathy_protocols.naturopathy && (
              <div id="protocol-naturopathy" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#e8f5e9', borderRadius: '12px', border: '2px solid #4caf50' }}>
                <h4 style={{ color: '#2e7d32', fontSize: '1.2rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🌱 Naturopathy (Natural Healing)
                  <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#4caf50', color: 'white', borderRadius: '12px' }}>
                    Natural
                  </span>
                </h4>

                {insights.multi_pathy_protocols.naturopathy.principles && (
                  <p style={{ color: '#37474f', marginBottom: '1rem', lineHeight: 1.6 }}>
                    {insights.multi_pathy_protocols.naturopathy.principles}
                  </p>
                )}

                {insights.multi_pathy_protocols.naturopathy.therapies?.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#2e7d32' }}>Recommended Therapies:</strong>
                    <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.5rem' }}>
                      {insights.multi_pathy_protocols.naturopathy.therapies.map((therapy, idx) => (
                        <div key={idx} style={{ padding: '0.75rem', background: 'white', borderRadius: '8px' }}>
                          <div style={{ fontWeight: 600, color: '#2e7d32' }}>{therapy.name}</div>
                          {therapy.description && <div style={{ fontSize: '0.9rem', color: '#37474f', marginTop: '0.25rem' }}>{therapy.description}</div>}
                          {therapy.benefits && <div style={{ fontSize: '0.85rem', color: '#607d8b', marginTop: '0.25rem' }}>Benefits: {therapy.benefits}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Lifestyle Medicine */}
            {insights.multi_pathy_protocols.lifestyle_medicine && (
              <div id="protocol-lifestyle_medicine" style={{ marginBottom: '2rem', padding: '1.5rem', background: '#e1f5fe', borderRadius: '12px', border: '2px solid #03a9f4' }}>
                <h4 style={{ color: '#01579b', fontSize: '1.2rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🏃 Lifestyle Medicine
                  <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#4caf50', color: 'white', borderRadius: '12px' }}>
                    High Evidence
                  </span>
                </h4>

                {insights.multi_pathy_protocols.lifestyle_medicine.interventions?.length > 0 && (
                  <div>
                    <strong style={{ color: '#01579b' }}>Evidence-Based Interventions:</strong>
                    <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.5rem' }}>
                      {insights.multi_pathy_protocols.lifestyle_medicine.interventions.map((intervention, idx) => (
                        <div key={idx} style={{ padding: '0.75rem', background: 'white', borderRadius: '8px' }}>
                          <div style={{ fontWeight: 600, color: '#01579b' }}>{intervention.category}</div>
                          {intervention.recommendations && (
                            <ul style={{ margin: '0.5rem 0 0 1.5rem', color: '#37474f', fontSize: '0.9rem' }}>
                              {intervention.recommendations.map((rec, ridx) => (
                                <li key={ridx}>{rec}</li>
                              ))}
                            </ul>
                          )}
                          {intervention.goals && (
                            <div style={{ fontSize: '0.85rem', color: '#607d8b', marginTop: '0.5rem' }}>
                              <strong>Goals:</strong> {intervention.goals}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Integration Recommendations */}
            {insights.multi_pathy_protocols.integration_recommendations && (
              <div style={{ padding: '1.5rem', background: '#f3e5f5', borderRadius: '12px', border: '2px solid #9c27b0' }}>
                <h4 style={{ color: '#6a1b9a', fontSize: '1.1rem', marginBottom: '1rem' }}>🔗 Integration Recommendations</h4>
                
                {insights.multi_pathy_protocols.integration_recommendations.synergies?.length > 0 && (
                  <div style={{ marginBottom: '1rem', padding: '1rem', background: 'white', borderRadius: '8px' }}>
                    <strong style={{ color: '#6a1b9a' }}>✅ Complementary Approaches:</strong>
                    <ul style={{ margin: '0.5rem 0 0 1.5rem', color: '#37474f' }}>
                      {insights.multi_pathy_protocols.integration_recommendations.synergies.map((synergy, idx) => (
                        <li key={idx}>{synergy}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {insights.multi_pathy_protocols.integration_recommendations.precautions?.length > 0 && (
                  <div style={{ marginBottom: '1rem', padding: '1rem', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ff9800' }}>
                    <strong style={{ color: '#e65100' }}>⚠️ Important Precautions:</strong>
                    <ul style={{ margin: '0.5rem 0 0 1.5rem', color: '#37474f' }}>
                      {insights.multi_pathy_protocols.integration_recommendations.precautions.map((precaution, idx) => (
                        <li key={idx}>{precaution}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {insights.multi_pathy_protocols.integration_recommendations.priority_order && (
                  <div style={{ padding: '1rem', background: 'white', borderRadius: '8px' }}>
                    <strong style={{ color: '#6a1b9a' }}>📋 Suggested Priority:</strong>
                    <p style={{ margin: '0.5rem 0 0 0', color: '#37474f' }}>
                      {insights.multi_pathy_protocols.integration_recommendations.priority_order}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Missing Data Suggestions */}
        {missing_data_suggestions?.length > 0 && (
          <div style={{ background: '#fff9e6', borderRadius: '12px', padding: '1.5rem', border: '1px solid #ffd54f' }}>
            <h3 style={{ color: '#f57f17', fontSize: '1.3rem', marginBottom: '1rem' }}>📋 Suggested Data to Complete</h3>
            {missing_data_suggestions.map((suggestion, idx) => (
              <div key={idx} style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: idx < missing_data_suggestions.length - 1 ? '1px solid #ffe082' : 'none' }}>
                <div style={{ fontWeight: 600, color: '#f57f17' }}>{suggestion.category}</div>
                <div style={{ fontSize: '0.9rem', color: '#37474f', marginTop: '0.25rem' }}>
                  <strong>Missing:</strong> {suggestion.specific_items?.join(', ')}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#607d8b', marginTop: '0.25rem' }}>{suggestion.importance}</div>
              </div>
            ))}
          </div>
        )}

        {/* Treatment Guidance Disclaimer */}
        {treatment_guidance && (
          <div style={{ background: '#e3f2fd', borderRadius: '12px', padding: '1.5rem', border: '1px solid #90caf9' }}>
            <h3 style={{ color: '#1565c0', fontSize: '1.1rem', marginBottom: '0.75rem' }}>ℹ️ Important Note</h3>
            <p style={{ margin: 0, color: '#37474f', fontSize: '0.95rem' }}>
              {treatment_guidance.disclaimer || 'This analysis is for informational purposes only and does not constitute medical advice. Please consult with your healthcare provider for personalized medical guidance.'}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard-container" data-testid="history-page">
      {/* Rate Limit Error Banner */}
      {rateLimitError && (
        <div style={{
          position: 'fixed',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: rateLimitError.type === 'rate_limit' ? '#fef3c7' : '#fee2e2',
          border: `1px solid ${rateLimitError.type === 'rate_limit' ? '#f59e0b' : '#ef4444'}`,
          borderRadius: '12px',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          zIndex: 1000,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          animation: 'slideDown 0.3s ease'
        }}>
          <AlertTriangle 
            size={20} 
            color={rateLimitError.type === 'rate_limit' ? '#f59e0b' : '#ef4444'} 
          />
          <span style={{ 
            color: rateLimitError.type === 'rate_limit' ? '#92400e' : '#991b1b',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {rateLimitError.message}
          </span>
          <button
            onClick={() => setRateLimitError(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              marginLeft: 'auto'
            }}
          >
            <X size={18} color={rateLimitError.type === 'rate_limit' ? '#92400e' : '#991b1b'} />
          </button>
        </div>
      )}

      {/* Animation Keyframes */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>

      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-brand">
          <Activity size={28} />
          MedPlexity
        </div>
        <div className="navbar-menu" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <CreditsDisplay refreshTrigger={refreshCredits} />
          <button 
            className="nav-link" 
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={18} /> Back
          </button>
        </div>
      </nav>

      <div className="content-section">
        <h2 className="section-title" data-testid="page-title">
          <Activity size={32} /> My History
        </h2>
        <p style={{ color: '#78909c', marginBottom: '2rem' }}>
          Your Patient Longitudinal Intelligence System
        </p>

        {/* 3-Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          borderBottom: '2px solid #e0e0e0',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveTab('reports')}
            style={{
              background: activeTab === 'reports' ? '#0277bd' : 'transparent',
              color: activeTab === 'reports' ? 'white' : '#78909c',
              border: 'none',
              padding: '1rem 2rem',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: '8px 8px 0 0',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <FileText size={20} />
            Reports Upload
          </button>
          
          <button
            onClick={() => setActiveTab('vitals')}
            style={{
              background: activeTab === 'vitals' ? '#0277bd' : 'transparent',
              color: activeTab === 'vitals' ? 'white' : '#78909c',
              border: 'none',
              padding: '1rem 2rem',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: '8px 8px 0 0',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Heart size={20} />
            Vitals & Personal Data
          </button>
          
          <button
            onClick={() => setActiveTab('insights')}
            style={{
              background: activeTab === 'insights' ? '#0277bd' : 'transparent',
              color: activeTab === 'insights' ? 'white' : '#78909c',
              border: 'none',
              padding: '1rem 2rem',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: '8px 8px 0 0',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Brain size={20} />
            Patient Insights
          </button>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'reports' && renderReportsTab()}
          {activeTab === 'vitals' && renderVitalsTab()}
          {activeTab === 'insights' && renderInsightsTab()}
        </div>

        {/* AI Insight Button - Floating */}
        {(reports.length > 0 || dataChanged) && (
          <button
            style={{
              position: 'fixed',
              bottom: '2rem',
              right: '2rem',
              background: dataChanged ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' : 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '50px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(76, 175, 80, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'transform 0.2s, box-shadow 0.2s',
              zIndex: 1000,
              animation: dataChanged ? 'pulse 2s infinite' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 6px 25px rgba(76, 175, 80, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(76, 175, 80, 0.4)';
            }}
            onClick={generateNewInsights}
          >
            <Sparkles size={20} />
            {dataChanged ? 'Data Changed - Generate Insights' : 'Generate AI Insights'}
          </button>
        )}
      </div>

      {/* Add animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinning {
          animation: spin 1s linear infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default History;
